import { useState } from 'react'
import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import ToggleField from '@/components/Indicators/ToggleField'
import TelegramNotificationsSection from '@/components/Indicators/TelegramNotificationsSection'
import SignalAlertToggle from '@/components/Indicators/SignalAlertToggle'
import SignalClassConfigFields from '@/components/Indicators/SignalClassConfigFields'
import {
  QUANTUM_COLORS,
  AVAILABLE_TIMEZONES,
  LINE_WIDTHS,
  PANEL_DISCLAIMER,
  resolveConfig,
  resolveColor,
  resetToDefaults,
} from './constants.js'

const SESSION_RE = /^([01]\d|2[0-3])[0-5]\d-([01]\d|2[0-3])[0-5]\d$/

// Sección acordeón con estado persistido en panelSections (molde PRQ/bollinger).
function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ ...config, panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

function ChipSelect({ label, options, value, onChange }) {
  return (
    <div className="space-y-1">
      <span className="text-xs text-slate-600 dark:text-gray-300">{label}</span>
      <div className="grid grid-cols-3 gap-1">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
              value === opt.value
                ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// Campo de sesión "HHMM-HHMM": edición libre en texto, solo se propaga al
// onChange (y por tanto a resolveConfig) cuando el formato es válido -- un
// valor a medio escribir nunca corrompe la config persistida.
function SessionField({ label, value, onChange }) {
  const [draft, setDraft] = useState(value)
  const valid = SESSION_RE.test(draft)
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-700 dark:text-gray-200">{label}</span>
      <input
        type="text"
        value={draft}
        placeholder="HHMM-HHMM"
        onChange={(e) => {
          const next = e.target.value
          setDraft(next)
          if (SESSION_RE.test(next)) onChange(next)
        }}
        onBlur={() => setDraft(value)} // si quedó inválido, vuelve al último valor válido persistido
        className={`w-28 px-2 py-1 text-[11px] rounded border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-right ${
          valid ? 'border-[var(--hair,rgba(176,141,87,0.35))]' : 'border-red-400'
        }`}
      />
    </div>
  )
}

export default function NasdaqQuantumPanel({ config, onChange, onClose, chartSymbol, chartTimeframe, signalAlerts, indicatorVersion }) {
  const cfg = resolveConfig(config)
  const update = (key, value) => onChange({ ...cfg, [key]: value })

  const resetAll = () => {
    if (typeof window !== 'undefined' && !window.confirm('¿Restablecer todos los valores de NASDAQ Quantum a sus defaults?')) return
    onChange(resetToDefaults(cfg))
  }

  return (
    <IndicatorConfigModal title="Configuración NASDAQ Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <p className="text-[10px] leading-relaxed px-1 py-2 rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-white/50 dark:bg-gray-900/20 text-slate-600 dark:text-gray-300">
          {PANEL_DISCLAIMER}
        </p>

        <Section title="Visualización" sectionKey="visual" config={cfg} onChange={onChange}>
          <ToggleField label="Caja del rango" value={cfg.showRangeBox} onChange={(v) => update('showRangeBox', v)} />
          <ToggleField label="Relleno del rango" value={cfg.showRangeFill} onChange={(v) => update('showRangeFill', v)} />
          <ToggleField label="Línea EMA" value={cfg.showEmaLine} onChange={(v) => update('showEmaLine', v)} />
          <ToggleField label="Marcadores LONG/SHORT" value={cfg.showSignalLabels} onChange={(v) => update('showSignalLabels', v)} />
          <ToggleField label="Niveles (entrada/SL/TP)" value={cfg.showLevels} onChange={(v) => update('showLevels', v)} />
          <ToggleField label="Últimas señales (tabla)" value={cfg.showLastSignals} onChange={(v) => update('showLastSignals', v)} />
        </Section>

        {/* Único grupo de parámetros del motor editable (WP §1/§5, decisión
            cerrada): el resto son constantes internas, igual que en el Pine
            origen (solo sessA/sessB/sessC/tz son `input.*` visibles ahí). */}
        <Section title="Sesiones" sectionKey="sessions" config={cfg} onChange={onChange}>
          <SessionField label="Rango" value={cfg.sessA} onChange={(v) => update('sessA', v)} />
          <SessionField label="Ventana de ruptura" value={cfg.sessB} onChange={(v) => update('sessB', v)} />
          <SessionField label="Ventana de cierre" value={cfg.sessC} onChange={(v) => update('sessC', v)} />
          <ChipSelect
            label="Zona horaria"
            options={AVAILABLE_TIMEZONES.map(tz => ({ value: tz, label: tz.split('/')[1] ?? tz }))}
            value={cfg.tz}
            onChange={(v) => update('tz', v)}
          />
          <p className="text-[10px] text-slate-500 dark:text-gray-400 leading-relaxed">
            Formato HHMM-HHMM (24h). Hora de apertura de vela, zona horaria real con cambio de hora.
          </p>
        </Section>

        {/* Clase de señal A/A+ (WP A/A+ fase 2): m y f de la fórmula de la
            tarjeta (utils/signalClass.js). Solo etiqueta, nunca oculta. */}
        <Section title="Clase de señal (A/A+)" sectionKey="signalClass" config={cfg} onChange={onChange}>
          <SignalClassConfigFields config={cfg} update={update} />
        </Section>

        <Section title="Notificaciones" sectionKey="notifications" config={cfg} onChange={onChange}>
          {/* WP-3 fase B: «Nueva señal» crea/borra una alerta real del motor
              (signal_any, 5m: el único TF del indicador en el catálogo y en
              la matriz). Solo aviso en el chart: el Telegram de NASDAQ ya lo
              da la suscripción de abajo (matriz), así no llega dos veces.
              Fuera los toggles que nadie leía (maestro, TP, SL). */}
          <SignalAlertToggle
            alerts={signalAlerts}
            indicatorId="nasdaq_quantum"
            indicatorName="NASDAQ Quantum"
            version={indicatorVersion}
            eventCodes={['signal_any']}
            symbol={chartSymbol}
            timeframe={chartTimeframe}
            fixedTimeframe="5m"
            timeframes={['5m']}
            config={cfg}
            telegram={false}
          />
          {/* Gate de Fase 2 (c877e8a) revertido: 'nasdaq_quantum' ya está en
              SUPPORTED_INDICATORS (frontend) y ALLOWED_INDICATORS (backend),
              y _SIGNAL_INDICATORS tiene motor real (WP conexión de señales,
              2026-09-24). fixedTimeframe="5m": el motor de backend es
              5m-nativo (PER_INDICATOR_TIMEFRAMES), igual que rango_quantum
              fuerza 4h -- el toggle contextual se suscribe SIEMPRE a 5m
              independientemente del TF que el usuario esté viendo. */}
          <TelegramNotificationsSection
            indicatorId="nasdaq_quantum"
            symbol={chartSymbol}
            timeframe={chartTimeframe}
            fixedTimeframe="5m"
          />
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="Rango" value={resolveColor(cfg.rangeColor, QUANTUM_COLORS.range)} onChange={(v) => update('rangeColor', v)} />
          <ColorPickerField label="EMA" value={resolveColor(cfg.emaColor, QUANTUM_COLORS.accent)} onChange={(v) => update('emaColor', v)} />
          <ColorPickerField label="LONG" value={resolveColor(cfg.longColor, QUANTUM_COLORS.bull)} onChange={(v) => update('longColor', v)} />
          <ColorPickerField label="SHORT" value={resolveColor(cfg.shortColor, QUANTUM_COLORS.bear)} onChange={(v) => update('shortColor', v)} />
          <ColorPickerField label="Entrada" value={resolveColor(cfg.entryColor, QUANTUM_COLORS.neutral)} onChange={(v) => update('entryColor', v)} />
          <ColorPickerField label="Stop Loss" value={resolveColor(cfg.slColor, QUANTUM_COLORS.bear)} onChange={(v) => update('slColor', v)} />
          <ColorPickerField label="Take Profit" value={resolveColor(cfg.tpColor, QUANTUM_COLORS.bull)} onChange={(v) => update('tpColor', v)} />
        </Section>

        <Section title="Estilo de línea" sectionKey="lineStyle" config={cfg} onChange={onChange}>
          <ChipSelect
            label="Grosor"
            options={LINE_WIDTHS.map(w => ({ value: w, label: String(w) }))}
            value={cfg.lineWidth}
            onChange={(v) => update('lineWidth', v)}
          />
        </Section>

        <button
          onClick={resetAll}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
        >
          <RotateCcw size={12} />
          Restablecer valores Quantum
        </button>

      </div>
    </IndicatorConfigModal>
  )
}
